import React, { useState } from 'react';
import { ShoppingCart, Search, X, Check } from 'lucide-react';
import { navItems } from './NavLinksData';
import { Link } from 'react-router-dom';
import { CartButton } from '../../../../ui/elements/CartButton';
import { useEffect, useRef } from 'react';
import PublicProfileMenu from './PublicProfileMenu';

const DesktopNav: React.FC = () => {
  const [showSearch, setShowSearch] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const searchRef = useRef<HTMLDivElement>(null);

  // Fermer le popup si on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSearch(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav className="hidden md:flex items-center justify-between w-full">
      {/* Groupe des liens de navigation */}
      <div className="flex space-x-8 items-center bg-white/10 backdrop-blur-sm rounded-full px-3">
        {navItems.map((item) => (
          <Link
            key={item.name}
            to={item.to}
            className="py-2 hover:text-accent transition-colors duration-200"
          >
            {item.name}
          </Link>
        ))}
      </div>

      {/* Groupe du panier et recherche */}
      <div className="flex items-center gap-4">
        <div className="relative" ref={searchRef}>
          <button
            onClick={() => setShowSearch(!showSearch)}
            className="p-2 rounded-full bg-white/30 hover:bg-white/50 transition"
          >
            {!showSearch ? <Search size={24} /> : <X size={24} />}
          </button>

          {/* Popup de recherche */}
          {showSearch && (
            <div className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-xl overflow-hidden z-50 animate-fade-in-up">
              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Rechercher..."
                  className="w-full px-4 py-3 pr-20 text-gray-800 placeholder:text-gray-400 bg-gray-100 focus:outline-none focus:bg-white transition-colors"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && searchTerm.trim()) {
                      window.location.href = `/products?search=${encodeURIComponent(searchTerm)}`;
                      setShowSearch(false);
                      setSearchTerm('');
                    }
                  }}
                />
                {/* X pour effacer */}
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-12 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X size={16} />
                  </button>
                )}

                {/* Icône entrée pour valider */}
                <button
                  onClick={() => {
                    if (searchTerm.trim()) {
                      window.location.href = `/products?search=${encodeURIComponent(searchTerm)}`;
                      setShowSearch(false);
                      setSearchTerm('');
                    }
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-accent rounded-lg p-1.5 text-primary hover:bg-accent/80 transition-colors"
                >
                  <Check size={20} />
                </button>
              </div>

              {/* Suggestions */}
              <div className="border-t border-gray-100">
                {[
                  "plantes médicinales",
                  "psaumes pour la paix",
                  "huiles essentielles",
                  "tisanes bio",
                  "méditation guidée"
                ].map((suggestion, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      setSearchTerm(suggestion);
                    }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-left text-sm text-gray-600 hover:bg-gray-50 transition-colors"
                  >
                    <span className="text-gray-400">↳</span>
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <CartButton />
        {/* Menu utilisateur externalisé */}
        <PublicProfileMenu />
      </div>
    </nav>
  );
};

export default DesktopNav;